/**
 * How a reply starts — Step 174.
 *
 * Kai: *"Hi {Name}, / XXX / {Signature} should be customisable, the XXX is
 * where the typing should automatically begin."*
 *
 * Every reply on a service desk has the same three parts and only the middle
 * one is the agent's actual work. Making somebody type the other two, forty
 * times a day, is forty small frictions between them and the answer — and it
 * is why replies drift into terse fragments with no greeting at all.
 *
 * Shared because the tokens have to mean the same thing in the browser (where
 * the composer renders them) and on the server (which owns the default and
 * will render them for automated replies later). One vocabulary, one renderer.
 */

/** The tokens a template may use. Kept deliberately tiny: a template language
 *  grows until nobody can predict what a template does. */
export const REPLY_TOKENS = {
  '{{name}}': "The requester's first name",
  '{{fullName}}': "The requester's full name",
  '{{ref}}': 'The ticket reference, like #INC-1042',
  '{{signature}}': 'Your email signature',
} as const;

export type ReplyToken = keyof typeof REPLY_TOKENS;

/**
 * The default. The blank line in the middle is not decoration — it is where
 * `{{cursor}}` would go if it existed, and the composer puts the caret in the
 * first empty paragraph for exactly this shape.
 */
export const DEFAULT_REPLY_TEMPLATE = '<p>Hi {{name}},</p><p></p><p>{{signature}}</p>';

export interface ReplyContext {
  /** Already-escaped display name, or null when the requester has none. */
  firstName: string | null;
  fullName: string | null;
  ref: string | null;
  /** Already-sanitised HTML. */
  signatureHtml: string | null;
}

/**
 * Substitute the tokens. A token with nothing to put in it is removed along
 * with its surrounding paragraph if that would leave the paragraph empty —
 * "Hi ," is worse than no greeting, and a bare rule where a signature should
 * be looks like a rendering fault.
 */
export function renderReplyTemplate(template: string, ctx: ReplyContext): string {
  let out = template;

  // Greeting: drop the whole paragraph when there is no name to use.
  if (!ctx.firstName) {
    out = out.replace(/<p>[^<]*\{\{(name|fullName)\}\}[^<]*<\/p>/g, '');
  }

  /**
   * A signature REPLACES its paragraph — Step 179.
   *
   * Kai, of the composer: *"the space is way larger in the ticket editor, as
   * there's a line that can't be clicked on or used."*
   *
   * A signature is block HTML — `lib/signature.ts` builds a `<div>`, and a
   * hand-written one is Tiptap's own paragraphs. Substituting that INSIDE
   * `<p>{{signature}}</p>` produces `<p><div>…</div></p>`, which no browser
   * accepts: the parser closes the paragraph before the div and then turns the
   * orphaned `</p>` into a SECOND empty paragraph. The composer was opening
   * with two blank lines and a dead one below the signature, while the admin
   * editor — where the token is still literal text — showed one. Same
   * template, different shape, and nothing on screen explained why.
   *
   * So the whole paragraph gives way to the signature. An empty signature
   * takes the paragraph with it, which is the rule this already had.
   */
  out = out.replace(/<p>\s*\{\{signature\}\}\s*<\/p>/g, ctx.signatureHtml ?? '');

  return out
    .replace(/\{\{name\}\}/g, ctx.firstName ?? '')
    .replace(/\{\{fullName\}\}/g, ctx.fullName ?? '')
    .replace(/\{\{ref\}\}/g, ctx.ref ?? '')
    .replace(/\{\{signature\}\}/g, ctx.signatureHtml ?? '');
}

/**
 * Does this template say where typing starts? — Step 179.
 *
 * The caret goes on the first empty paragraph, and an admin who deletes the
 * blank line has quietly moved it to the very end — below the signature,
 * which is never what they meant. The editor cannot mark a line that is not
 * there, so the settings page says so in words instead.
 */
export function hasCaretGap(template: string): boolean {
  return /<p>(?:\s|<br\s*\/?>|&nbsp;)*<\/p>/i.test(template);
}

/** The first name, the way a person writes it. */
export function firstNameOf(name: string | null | undefined): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first || null;
}
